
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Bold, Italic, List, ListOrdered, Image as ImageIcon, Loader2, Indent, Outdent, Palette, Trash2, Type, Table as TableIcon, Heading1, Quote, Code } from 'lucide-react';
import { compressImage } from '../../lib/utils';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const ImageResizer = ({ img, editorRef, onResize, onDeselect }: {
    img: HTMLImageElement; editorRef: React.RefObject<HTMLDivElement>; onResize: () => void; onDeselect: () => void;
}) => {
    const [dim, setDim] = useState({ w: 0, h: 0, t: 0, l: 0 });
    const requestRef = useRef<number | null>(null);

    const updateOverlay = useCallback(() => {
        if (!img || !editorRef.current || !img.isConnected) { onDeselect(); return; }
        const editorRect = editorRef.current.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        
        setDim({ 
            w: imgRect.width, 
            h: imgRect.height, 
            t: imgRect.top - editorRect.top, 
            l: imgRect.left - editorRect.left 
        });
        
        requestRef.current = requestAnimationFrame(updateOverlay);
    }, [img, editorRef, onDeselect]);

    useEffect(() => {
        requestRef.current = requestAnimationFrame(updateOverlay);
        return () => { if (requestRef.current !== null) cancelAnimationFrame(requestRef.current); };
    }, [updateOverlay]);

    const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault(); e.stopPropagation();
        const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const startW = dim.w;
        const move = (ev: MouseEvent | TouchEvent) => {
            const clientX = 'touches' in ev ? ev.touches[0].clientX : (ev as MouseEvent).clientX;
            let newW = Math.max(50, startW + (clientX - startX));
            img.style.width = `${newW}px`; img.style.height = 'auto'; 
        };
        const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up); onResize(); };
        window.addEventListener('mousemove', move, { passive: false }); window.addEventListener('mouseup', up); window.addEventListener('touchmove', move, { passive: false }); window.addEventListener('touchend', up);
    };

    return (
        <div className="absolute border-2 border-blue-500 z-10 pointer-events-none" style={{ top: dim.t, left: dim.l, width: dim.w, height: dim.h }}>
            <button className="absolute -top-3 -right-3 w-7 h-7 bg-white text-red-500 shadow-md rounded-full flex items-center justify-center pointer-events-auto border border-gray-100" onClick={(e) => { e.stopPropagation(); img.remove(); onResize(); onDeselect(); }}><Trash2 size={14} /></button>
            <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-500 border-2 border-white rounded-full cursor-se-resize pointer-events-auto" onMouseDown={handleStart} onTouchStart={handleStart}></div>
        </div>
    );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isProcessingImg, setIsProcessingImg] = useState(false);
    const [selectedImg, setSelectedImg] = useState<HTMLImageElement | null>(null);
    const isComposing = useRef(false);
    const savedSelection = useRef<Range | null>(null);

    useEffect(() => {
        if (editorRef.current && document.activeElement !== editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    const handleInput = () => {
        if (editorRef.current && !isComposing.current) {
            const html = editorRef.current.innerHTML;
            onChange(html === '<br>' ? '' : html);
        }
    };

    const execCmd = (command: string, value: string | undefined = undefined) => {
        if (document.activeElement !== editorRef.current) {
            if (savedSelection.current) {
                const sel = window.getSelection();
                if (sel) { sel.removeAllRanges(); sel.addRange(savedSelection.current); }
            } else { editorRef.current?.focus(); }
        }
        document.execCommand(command, false, value);
        handleInput();
    };
    
    // Improved Format Block handling for Headers
    const formatBlock = (tag: string) => {
        execCmd('formatBlock', tag);
    };

    const insertImage = async (file: File) => {
        setIsProcessingImg(true);
        try {
            const compressedBase64 = await compressImage(file);
            const imgHtml = `<img src="${compressedBase64}" loading="lazy" class="rich-img-inline" />`;
            editorRef.current?.focus();
            document.execCommand('insertHTML', false, imgHtml);
            handleInput();
        } finally { setIsProcessingImg(false); }
    };

    // Insert simple table
    const insertTable = () => {
        const tableHtml = `
            <table style="width:100%; border-collapse: collapse; margin: 1em 0;">
                <thead>
                    <tr>
                        <th style="border:1px solid #e2e8f0; padding:8px; background:#f8fafc;">标题 1</th>
                        <th style="border:1px solid #e2e8f0; padding:8px; background:#f8fafc;">标题 2</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style="border:1px solid #e2e8f0; padding:8px;">内容 1</td>
                        <td style="border:1px solid #e2e8f0; padding:8px;">内容 2</td>
                    </tr>
                </tbody>
            </table>
            <p><br/></p>
        `;
        editorRef.current?.focus();
        document.execCommand('insertHTML', false, tableHtml);
        handleInput();
    };

    const saveRange = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) savedSelection.current = sel.getRangeAt(0);
    };

    const restoreRange = () => {
        if (savedSelection.current) {
             const sel = window.getSelection();
             sel?.removeAllRanges();
             sel?.addRange(savedSelection.current);
        }
    };
    
    // Custom Paste Handler to clean up dirty styles but keep structure
    const handlePaste = (e: React.ClipboardEvent) => {
        // We generally allow default paste behavior as browsers are getting better,
        // but for "Dark Mode" copies, we might want to force color reset if needed.
        // For now, relying on the robust CSS classes to override inline styles where possible.
        // If images are pasted, we handle them via the onPaste prop on the div which defaults to browser behavior for files?
        // Actually, for files, we use the `onPaste` below.
        
        // This is mainly for file paste. Text/HTML paste is handled by browser.
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                e.preventDefault(); 
                const file = items[i].getAsFile(); 
                if (file) insertImage(file);
                return;
            }
        }
    };

    const ToolbarButton = ({ icon: Icon, cmd, arg, title, onClick }: any) => (
        <button 
            onMouseDown={(e) => e.preventDefault()} 
            onClick={onClick || (() => execCmd(cmd, arg))} 
            className="p-1.5 hover:bg-gray-200 rounded text-slate-600 min-w-[28px] flex items-center justify-center transition-colors" 
            title={title} 
            type="button"
        >
            {Icon && <Icon size={16}/>}
        </button>
    );

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white flex flex-col focus-within:ring-2 focus-within:ring-blue-500 relative h-full w-full shadow-sm">
            <div className="flex items-center gap-1 p-2 border-b border-gray-100 bg-gray-50/80 backdrop-blur overflow-x-auto flex-wrap flex-shrink-0">
                <div className="flex items-center gap-0.5 border-r border-gray-200 pr-2 mr-2">
                    <div className="relative flex items-center justify-center p-1.5 hover:bg-gray-200 rounded text-slate-600 group w-8 cursor-pointer" title="标题">
                        <Heading1 size={16} />
                        <select 
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                            defaultValue=""
                            onMouseDown={saveRange}
                            onChange={(e) => { restoreRange(); formatBlock(e.target.value); e.target.value=""; }}
                        >
                            <option value="">正文</option>
                            <option value="H1">标题 1</option>
                            <option value="H2">标题 2</option>
                            <option value="H3">标题 3</option>
                        </select>
                    </div>
                </div>

                <ToolbarButton icon={Bold} cmd="bold" title="加粗" />
                <ToolbarButton icon={Italic} cmd="italic" title="斜体" />
                <ToolbarButton icon={Quote} onClick={() => formatBlock('blockquote')} title="引用" />
                <ToolbarButton icon={Code} cmd="formatBlock" arg="pre" title="代码块" />
                
                <div className="relative flex items-center justify-center p-1.5 hover:bg-gray-200 rounded text-slate-600" title="字体颜色">
                    <Palette size={16} />
                    <input type="color" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                        onMouseDown={saveRange} 
                        onChange={(e) => { restoreRange(); execCmd('foreColor', e.target.value); }} 
                    />
                </div>

                <div className="relative flex items-center justify-center p-1.5 hover:bg-gray-200 rounded text-slate-600 group w-8" title="字号大小">
                    <Type size={16} />
                    <select 
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                        defaultValue="3"
                        onMouseDown={saveRange}
                        onChange={(e) => { restoreRange(); execCmd('fontSize', e.target.value); }}
                    >
                        <option value="1">极小</option>
                        <option value="2">小</option>
                        <option value="3">默认</option>
                        <option value="4">中等</option>
                        <option value="5">大</option>
                        <option value="6">超大</option>
                        <option value="7">特大</option>
                    </select>
                </div>

                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                <ToolbarButton icon={List} cmd="insertUnorderedList" title="列表" />
                <ToolbarButton icon={ListOrdered} cmd="insertOrderedList" title="有序列表" />
                <ToolbarButton icon={Outdent} cmd="outdent" title="减少缩进" />
                <ToolbarButton icon={Indent} cmd="indent" title="增加缩进" />
                
                <div className="w-px h-4 bg-gray-300 mx-1"></div>
                
                <ToolbarButton icon={TableIcon} onClick={insertTable} title="插入表格" />
                
                <label className={`p-1.5 hover:bg-gray-200 rounded text-slate-600 cursor-pointer flex items-center ${isProcessingImg ? 'opacity-50' : ''}`} title="插入图片">
                    {isProcessingImg ? <Loader2 size={16} className="animate-spin"/> : <ImageIcon size={16}/>}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { if(e.target.files?.[0]) { insertImage(e.target.files[0]); e.target.value = ''; } }} />
                </label>
            </div>
            
            <style>{`
                .rich-editor-content { 
                    font-family: ui-sans-serif, system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", sans-serif;
                    font-size: 16px; 
                    color: #334155;
                    line-height: 1.7;
                }
                .rich-editor-content p, .rich-editor-content div { margin: 0.5em 0; }
                
                /* Headings */
                .rich-editor-content h1 { font-size: 1.75em; font-weight: 800; color: #1e293b; margin-top: 1em; margin-bottom: 0.6em; line-height: 1.2; }
                .rich-editor-content h2 { font-size: 1.4em; font-weight: 700; color: #1e293b; margin-top: 1.2em; margin-bottom: 0.6em; border-bottom: 2px solid #f1f5f9; padding-bottom: 0.3em; }
                .rich-editor-content h3 { font-size: 1.2em; font-weight: 700; color: #334155; margin-top: 1em; margin-bottom: 0.5em; }
                
                /* Typography */
                .rich-editor-content strong, .rich-editor-content b { font-weight: 700; color: #0f172a; }
                .rich-editor-content em, .rich-editor-content i { font-style: italic; color: #475569; }
                
                /* Lists */
                .rich-editor-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
                .rich-editor-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
                .rich-editor-content li { margin-bottom: 0.25em; }
                
                /* Quotes */
                .rich-editor-content blockquote { 
                    border-left: 4px solid #6366f1; 
                    background: #f5f3ff; 
                    padding: 12px 16px; 
                    margin: 1em 0; 
                    border-radius: 0 8px 8px 0;
                    color: #4b5563;
                    font-style: italic;
                }
                
                /* Code */
                .rich-editor-content pre { 
                    background: #1e293b; 
                    color: #f8fafc; 
                    padding: 1em; 
                    border-radius: 8px; 
                    overflow-x: auto; 
                    margin: 1em 0; 
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                    font-size: 0.9em;
                }
                .rich-editor-content code { 
                    background: #f1f5f9; 
                    color: #d63384; 
                    padding: 0.2em 0.4em; 
                    border-radius: 4px; 
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                    font-size: 0.9em;
                    border: 1px solid #e2e8f0;
                }

                /* Tables */
                .rich-editor-content table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1em 0;
                    font-size: 0.95em;
                }
                .rich-editor-content th, .rich-editor-content td {
                    border: 1px solid #e2e8f0;
                    padding: 8px 12px;
                    text-align: left;
                }
                .rich-editor-content th {
                    background-color: #f8fafc;
                    font-weight: 700;
                    color: #334155;
                }
                .rich-editor-content tr:nth-child(even) {
                    background-color: #fcfcfc;
                }

                /* Images */
                .rich-editor-content img { 
                    display: inline-block !important; 
                    vertical-align: middle !important;
                    margin: 4px 0;
                    max-width: 100%;
                    border-radius: 8px;
                    border: 1px solid #f1f5f9;
                }

                .rich-editor-placeholder {
                    font-size: 16px;
                }
            `}</style>
            
            <div className="relative flex-1 flex flex-col min-h-0 overflow-hidden">
                <div 
                    ref={editorRef}
                    className="flex-1 p-4 outline-none overflow-y-auto rich-editor-content"
                    contentEditable
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onCompositionStart={() => isComposing.current = true}
                    onCompositionEnd={() => { isComposing.current = false; handleInput(); }}
                    onClick={(e) => setSelectedImg(e.target instanceof HTMLImageElement ? e.target : null)}
                    style={{ whiteSpace: 'pre-wrap' }}
                />
                {selectedImg && <ImageResizer img={selectedImg} editorRef={editorRef} onResize={handleInput} onDeselect={() => setSelectedImg(null)} />}
            </div>
            {(!value && !editorRef.current?.innerText.trim()) && <div className="absolute top-[60px] left-4 text-slate-300 pointer-events-none rich-editor-placeholder">{placeholder || "记录学习心得，或粘贴 AI 整理的知识点..."}</div>}
        </div>
    );
};

export default RichTextEditor;
